import { toValue, type MaybeRefOrGetter, type Ref } from 'vue'
import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'

export default class SmartCardCoverage {
  static use(
    quote: MaybeRefOrGetter<QuotedPlanVm>,
    content: Ref<any>,
    sidebarIds: {
      doctors: MaybeRefOrGetter<string>
      drugCosts: MaybeRefOrGetter<string>
      healthcareCosts: MaybeRefOrGetter<string>
    }
  ) {
    const { show } = DialogController.use()
    const { quotingConfig } = QuotingConfig.use()

    const onUpdateNeeds = () => {
      show('update-insurance-sidebar')
    }

    const mysteryIndicator = defineAsyncComponent(
      () => import('~/components/quote/QuoteMysteryIndicator.vue')
    )
    const costMeter = defineAsyncComponent(() => import('~/components/quote/QuoteCostMeter.vue'))

    const coverageValues = computed((): Record<string, any> => {
      const plan = toValue(quote)
      return {
        monthly_premium: {
          value: `${formatUsd(plan.details.premium)}`,
          benchmark: plan.benchmarks?.monthlyPremium
        },
        doctors_in_network: {
          value: `${plan.providerCoverage.inNetworkCount} of ${plan.providerCoverage.count}`,
          benchmark: plan.benchmarks?.doctorsInNetwork,
          onClick: () => {
            if (plan.hasProviders) show(toValue(sidebarIds.doctors))
          },
          emptyState: mysteryIndicator,
          emptyAction: {
            do: onUpdateNeeds,
            text: 'Add Your Doctors',
            condition: !plan.hasProviders
          }
        },
        drug_costs: {
          value: `${formatUsd(plan.drugCost / 12)}`,
          benchmark: plan.benchmarks?.drugCosts,
          onClick: () => {
            if (plan.hasDrugs) show(toValue(sidebarIds.drugCosts))
          },
          emptyState: mysteryIndicator,
          emptyAction: {
            do: onUpdateNeeds,
            text: 'Add Your Drugs',
            condition: !plan.hasDrugs
          }
        },
        healthcare_costs: {
          component: costMeter,
          benchmark: plan.benchmarks?.healthcareCosts,
          onClick: () => show(toValue(sidebarIds.healthcareCosts))
        }
      }
    })

    const coverageItems = computed(() =>
      content.value.data?.coverage_items
        .filter(
          (x: any) => !!quotingConfig.value.smartCardShowTotalCosts || x.id !== 'healthcare_costs'
        )
        .map((x: any) => ({
          ...x,
          ...coverageValues.value[x.id]
        }))
    )

    return { coverageItems, onUpdateNeeds }
  }
}
